import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { site } from "../content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    if (id) lenis?.scrollTo(`#${id}`, { offset: -96, duration: 1.4 });
    else lenis?.scrollTo(0, { duration: 1.2 });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-[#FBF8F4]/85 backdrop-blur-xl border-b border-[#EFE6DC] shadow-[0_8px_30px_rgba(43,39,36,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => go(e, null)}
          className="flex items-center"
          data-testid="nav-logo-link"
          aria-label="By Milja V, etusivulle"
        >
          <img src="/images/logo-mark.png" alt="By Milja V" className="h-14 w-14 rounded-full" />
        </a>

        <nav className="hidden md:flex items-center gap-8" aria-label="Päävalikko">
          {site.nav.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => go(e, item.id)}
              className="text-sm text-[#5C564F] hover:text-[#2B2724] transition-colors duration-200"
              data-testid={`nav-link-${item.id}`}
            >
              {item.label}
            </a>
          ))}
          <a
            href="#yhteys"
            onClick={(e) => go(e, "yhteys")}
            className="rounded-full bg-[#7C8C7E] text-[#FBF8F4] text-sm font-medium px-6 py-2.5 hover:bg-[#5E6D60] transition-colors duration-200"
            data-testid="nav-cta-button"
          >
            {site.navCta}
          </a>
        </nav>

        <button
          className="md:hidden w-11 h-11 rounded-full border border-[#EFE6DC] bg-[#FBF8F4]/70 flex items-center justify-center text-[#2B2724]"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Sulje valikko" : "Avaa valikko"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden mx-4 mb-4 rounded-3xl bg-[#FBF8F4] border border-[#EFE6DC] shadow-[0_24px_60px_rgba(43,39,36,0.12)] p-6 flex flex-col gap-1"
            aria-label="Mobiilivalikko"
            data-testid="mobile-menu"
          >
            {site.nav.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => go(e, item.id)}
                className="py-3.5 px-3 rounded-xl text-lg font-serif text-[#2B2724] hover:bg-[#EFE6DC]/60 transition-colors duration-200"
                data-testid={`mobile-nav-link-${item.id}`}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#yhteys"
              onClick={(e) => go(e, "yhteys")}
              className="mt-3 rounded-full bg-[#7C8C7E] text-[#FBF8F4] text-center font-medium px-6 py-3.5 hover:bg-[#5E6D60] transition-colors duration-200"
              data-testid="mobile-nav-cta-button"
            >
              {site.navCta}
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
